import { Controller } from "@hotwired/stimulus";
import {
  DropHandler,
  FabricEditor,
  type FontsConfig,
  type LayerData,
} from "@frabr/fabric-editor";
import type { FabricObject } from "fabric";
import { LegacyFabricControls } from "../lib/legacy/fabric_controls";
import { LegacyDocument } from "../lib/legacy/document";
import { UndoManager } from "../lib/undo_manager";
import { MediaPicker } from "../lib/media_picker";
import { uploadToLibrary } from "../lib/media_library";
import { hasResolvableLegacyClip, streamShapeCatalogs } from "../lib/shape_catalogs";
import { mediumMeta } from "../lib/composition_document";
import {
  attachBoundTextEditing,
  bindSelectedField,
  interpolateBoundFields,
  withExpressions,
} from "../lib/fabric_bindings";

/**
 * LEGACY_EDITOR — l'ancien éditeur d'images (master : fabric_controller + _form_legacy) sur
 * un document de Composition, avec la lib actuelle (docs/plans/legacy-editor.md). Un canvas
 * = la page de l'îlot, une barre du bas, la barre flottante en place (lib/legacy/
 * fabric_controls) ; ni toolbox, ni autosave, ni pages.
 *
 * Il tient le document entier (lib/legacy/document) et n'écrit que sa page : Enregistrer
 * l'exporte, PATCHe le document du brouillon, le commite, puis sort ; Quitter jette le
 * brouillon (confirmation si le document a changé). La sortie suit l'hôte : la modale
 * (SidePanelHelper#editor_modal) se ferme, la pleine page visite `Location`.
 *
 * Le document garde ses tokens : le canvas montre les valeurs de CE lecteur (lib
 * fabric_bindings), et l'export rend les expressions. Les compos issues des templates
 * (liaisons, conditions) s'affichent donc juste, sans UI pour lier.
 *
 * Naming: "legacy-fabric" in HTML.
 */
export default class extends Controller<HTMLElement> {
  static targets = [
    "canvas", "container", "previewArea", "layerControls",
    "undoButton", "redoButton", "saveButton", "maskButton",
  ];

  static values = {
    width: Number,
    height: Number,
    sourceData: Object,
    slideIndex: { type: Number, default: 0 },
    fonts: Object,
    // Les URLs des catalogues de formes (global + chartes, lib/shape_catalogs.ts) — la lib n'en
    // embarque aucune, les clipShape du stock s'y résolvent.
    shapeCatalogs: Array,
    // Les valeurs de CE lecteur pour les champs liés — jamais écrites dans le document.
    variables: Object,
    defaultColor: String,
    // La médiathèque du lecteur, où entre un fichier lâché sur le canvas.
    uploadEndpoint: { type: String, default: "/toolbox/content_media" },
    // Le contrat du brouillon : le document, le commit, l'abandon (lib/legacy/document).
    documentUrl: String,
    commitUrl: String,
    discardUrl: String,
    unsavedMessage: String,
    // Hébergé par la modale du publisher : la sortie ferme l'hôte au lieu de visiter.
    panel: Boolean,
  };

  declare readonly canvasTarget: HTMLCanvasElement;
  declare readonly containerTarget: HTMLElement;
  declare readonly previewAreaTarget: HTMLElement;
  declare readonly layerControlsTarget: HTMLElement;
  declare readonly undoButtonTarget: HTMLButtonElement;
  declare readonly redoButtonTarget: HTMLButtonElement;
  declare readonly saveButtonTarget: HTMLButtonElement;
  declare readonly maskButtonTarget: HTMLElement;
  declare readonly hasMaskButtonTarget: boolean;

  declare readonly widthValue: number;
  declare readonly heightValue: number;
  declare readonly sourceDataValue: Record<string, unknown>;
  declare readonly slideIndexValue: number;
  declare readonly fontsValue: FontsConfig;
  declare readonly shapeCatalogsValue: string[];
  declare readonly variablesValue: Record<string, string>;
  declare readonly defaultColorValue: string;
  declare readonly uploadEndpointValue: string;
  declare readonly documentUrlValue: string;
  declare readonly commitUrlValue: string;
  declare readonly discardUrlValue: string;
  declare readonly unsavedMessageValue: string;
  declare readonly panelValue: boolean;

  private editor!: FabricEditor;
  private controls!: LegacyFabricControls;
  private document!: LegacyDocument;
  private undoManager!: UndoManager;
  private picker!: MediaPicker;
  private dropHandler!: DropHandler;
  private detachBoundTextEditing?: () => void;
  private unregisterShortcuts?: () => void;
  private dirty = false;
  private left = false;
  private readonly onDialogCancel = (e: Event) => {
    e.preventDefault();
    this.quit();
  };

  async connect(): Promise<void> {
    this.document = new LegacyDocument({
      doc: this.sourceDataValue,
      slideIndex: this.slideIndexValue,
      documentUrl: this.documentUrlValue,
      commitUrl: this.commitUrlValue,
      discardUrl: this.discardUrlValue,
    });

    // La bibliothèque (modal edit_dialog) : ajouter, ou remplacer l'image du cadre sélectionné
    this.picker = new MediaPicker(window.document.getElementById("edit_dialog"));
    this.picker.on("fabric:add", async (cm: { id?: number; url?: string }) => {
      await this.controls.addImage(cm);
      this.afterChange();
    });
    this.picker.on("fabric:replace", (cm: { id?: number; url?: string }) => this.controls.replaceSelectedImage(cm));

    this.editor = new FabricEditor(this.canvasTarget, {
      width: this.widthValue,
      height: this.heightValue,
      defaultColor: this.defaultColorValue,
      container: this.containerTarget,
      fonts: this.fontsValue,
    });
    await this.editor.replaceAllLayers(this.document.layers() as LayerData[]);
    this.interpolate();

    this.undoManager = new UndoManager({
      serialize: () => this.editor.persistence.exportLayersJSON(),
      restore: async (snapshot: string) => {
        await this.editor.replaceAllLayers(JSON.parse(snapshot));
        this.interpolate();
        this.controls.hide();
      },
    });
    this.undoManager.onStateChange = (state: { canUndo: boolean; canRedo: boolean }) => {
      this.undoButtonTarget.disabled = !state.canUndo;
      this.redoButtonTarget.disabled = !state.canRedo;
    };
    this.undoManager.initialize();

    this.controls = new LegacyFabricControls(this.editor, this.layerControlsTarget, {
      onModified: () => this.afterChange(),
      ...this.editor.getControlsConfig(this.previewAreaTarget),
    });
    this.controls.attach();
    this.editor.onResize(() => this.controls.repositionActive());

    // Un texte lié s'édite dans sa textbox : à la sortie, export et ré-affichage
    this.detachBoundTextEditing = attachBoundTextEditing(this.editor, () => {
      this.afterChange();
      this.interpolate();
    });

    // Un fichier lâché sur le canvas s'affiche tout de suite et entre dans la bibliothèque
    // pendant ce temps : le calque porte son CM.
    this.dropHandler = new DropHandler(this.editor, {
      hoverDelay: 500,
      getImageUrl: (file: File) => URL.createObjectURL(file),
      resolveFile: async (file: File) => {
        const cm = await uploadToLibrary(file, this.uploadEndpointValue);
        return { url: cm.url, imageMeta: mediumMeta(cm) };
      },
      onSuccess: () => this.afterChange(),
      onError: (error: unknown) => console.error("Erreur lors du traitement de l'image:", error),
    });
    this.dropHandler.attach(this.containerTarget);

    // La table du canvas (supprimer, dupliquer, copier-coller…) est dans la lib ; l'historique
    // est à l'hôte.
    this.unregisterShortcuts = this.editor.shortcuts.register([
      { id: "undo", keys: ["mod+z"], group: "edit", run: () => this.undo() },
      { id: "redo", keys: ["mod+shift+z", "mod+y"], group: "edit", run: () => this.redo() },
    ]);

    // Dans la modale, Échap est un Quitter : le brouillon ne se ferme pas sans se jeter.
    this.dialog?.addEventListener("cancel", this.onDialogCancel);

    // Les formes arrivent après l'éditeur (lib/shape_catalogs) : un clip legacy affiché en
    // rectangle se remonte quand son catalogue est là.
    streamShapeCatalogs(this.shapeCatalogsValue, () => this.remountLegacyClips());
  }

  disconnect(): void {
    this.dialog?.removeEventListener("cancel", this.onDialogCancel);
    this.unregisterShortcuts?.();
    this.detachBoundTextEditing?.();
    this.dropHandler?.detach();
    this.picker?.dispose();
    this.controls?.destroy();
    this.editor?.dispose();
  }

  // ==================== Barre du bas ====================

  addText(): void {
    this.controls.addText({ text: this.t("default_text", "Tapez votre texte ici") });
    this.afterChange();
  }

  addShape(): void {
    this.controls.addShape({ fill: this.defaultColorValue || undefined });
    this.afterChange();
  }

  /** Une variable = un texte LIÉ à son token (scope company) : le document garde `$NOM`, le
   *  canvas montre la valeur de ce lecteur. */
  addVariable(e: Event): void {
    const token = (e.currentTarget as HTMLElement).dataset.variable;
    if (!token) return;

    const textObj = this.controls.addText({ text: token, placeholder: false }) as unknown as FabricObject;
    // Ancré à gauche : la valeur interpolée s'étend vers la droite
    textObj.set({ originX: "left", textAlign: "left" });
    this.editor.canvas.setActiveObject(textObj);
    bindSelectedField(this.editor, { field: "text", expr: token, scope: "company" });
    this.interpolate();
    this.afterChange();
  }

  pickAddImage(): void {
    this.picker.open("fabric:add");
  }

  pickReplaceImage(): void {
    this.picker.open("fabric:replace");
  }

  async applyMask(): Promise<void> {
    if (!this.hasMaskButtonTarget) return;
    const maskUrl = this.maskButtonTarget.dataset.maskUrl;
    if (!maskUrl) return;

    await this.editor.masks.applyMask(maskUrl);
    this.maskButtonTarget.hidden = true;
    this.afterChange();
  }

  async undo(): Promise<void> {
    await this.undoManager.undo();
  }

  async redo(): Promise<void> {
    await this.undoManager.redo();
  }

  // ==================== Barre flottante ====================

  fcDelete(): void { this.controls.deleteSelection(); }
  fcSwitchShape(): void { this.controls.switchShape(); }
  fcBringForward(): void { this.controls.bringForward(); }
  fcSendBackward(): void { this.controls.sendBackward(); }
  fcOpenColorPicker(): void { this.controls.openColorPicker(); }
  fcReposition(): void { this.controls.startReposition(); }

  fcChangeColor(e: Event): void {
    const el = e.currentTarget as HTMLInputElement;
    this.controls.changeColor(el.dataset.color || el.value);
  }

  fcChangeOpacity(e: Event): void {
    this.controls.changeOpacity(Number((e.currentTarget as HTMLInputElement).value));
  }

  fcChangeFont(e: Event): void {
    const { fontFamily, fontWeight } = (e.currentTarget as HTMLElement).dataset;
    if (fontFamily) this.controls.changeFont(fontFamily, fontWeight);
  }

  // ==================== Brouillon : enregistrer / quitter ====================

  /** La page reçoit le canvas, le document part en base, le brouillon se commite, on sort. */
  async save(): Promise<void> {
    if (this.left) return;
    this.saveButtonTarget.disabled = true;
    this.editor.canvas.discardActiveObject();

    const saved = await this.document.save(this.exportLayers());
    const location = saved ? await this.document.commit() : false;
    if (location === false) {
      this.saveButtonTarget.disabled = false;
      return;
    }
    this.leave(location);
  }

  /** Quitter : confirmation si le document a changé, puis le brouillon se jette. */
  async quit(): Promise<void> {
    if (this.left) return;
    if (this.dirty && !window.confirm(this.unsavedMessageValue)) return;

    const location = await this.document.discard();
    if (location === false) return;
    this.leave(location);
  }

  /** Ce qu'on EXPORTE est le document, pas son rendu chez ce lecteur (lib fabric_bindings). */
  private exportLayers(): object[] {
    return withExpressions(JSON.parse(this.editor.persistence.exportLayersJSON()));
  }

  /** Remonte le canvas si un clip par id seul vient de devenir résoluble (rare, à l'ouverture). */
  private async remountLegacyClips(): Promise<void> {
    if (this.left || !this.element.isConnected) return;

    const layers = JSON.parse(this.editor.persistence.exportLayersJSON());
    if (!hasResolvableLegacyClip(layers)) return;

    await this.editor.replaceAllLayers(layers);
    this.interpolate();
    this.undoManager.initialize();
  }

  /** Après chaque mutation : un cran d'historique, et le document compte comme modifié. */
  private afterChange(): void {
    this.undoManager.push();
    this.dirty = true;
  }

  /** Le canvas montre ce que CE lecteur verra, le document garde ses tokens. */
  private interpolate(): void {
    interpolateBoundFields(this.editor, this.variablesValue || {});
  }

  /** La sortie : la modale se ferme et se vide (le contrôleur se débranche), la pleine page
   *  visite `Location` en replace — un brouillon détruit ne reste pas dans l'historique. */
  private leave(location: string | null): void {
    this.left = true;
    const dialog = this.dialog;
    if (dialog) {
      dialog.querySelector("dialog")?.close();
      const frame = this.element.closest("turbo-frame");
      if (frame) {
        frame.removeAttribute("src");
        frame.removeAttribute("complete");
        frame.innerHTML = "";
      }
    } else if (location) {
      window.Turbo?.visit(location, { action: "replace" });
    }
  }

  /** L'enveloppe modale de l'éditeur (SidePanelHelper#editor_modal), s'il y est. */
  private get dialog(): HTMLElement | null {
    return this.element.closest<HTMLElement>("[data-controller~='dialog']");
  }

  // Récupère une traduction du namespace js.fabric exposé via window.jsTranslations
  private t(key: string, fallback: string): string {
    return (window as any).jsTranslations?.fabric?.[key] ?? fallback;
  }
}
